/**
 * Standard rate sets — the tenants.config `billing.rate_sets` reader
 * (the FE sibling of api/_shared/rateSets.ts readRateSets; same walk,
 * kept in lockstep by the shared unit fixtures) plus the wire helpers
 * behind the AddClientDialog picker, the Rates card's "Apply a rate
 * set", and the Admin > Client rates editor.
 *
 * A rate set is a named list of amounts, one per priceable band. It is
 * PRE-FILL: applying one writes explicit client_rates rows through the
 * ordinary rates write path. Rate resolution never reads it, and a
 * band a set leaves blank stays "No rate on file" for the client.
 *
 * NO PLATFORM DEFAULT: an unset or malformed key reads as an EMPTY
 * list and every picker hides itself. Amounts are money STRINGS.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from '@/lib/api';
import { contactSentence, saveFailureText, type SupportContact } from '@/lib/errorCopy';
import type { RateBandOption } from '@/lib/notes';
import type { ClientRateRowWire } from '@/lib/clientRates';

export interface RateSetWire {
  id: string;
  name: string;
  /** band token -> canonical 2-decimal amount (string; never a float). */
  rates: Record<string, string>;
}

/** The id shape: lowercase slug, 1-64 chars (`standard_2026`). */
export const RATE_SET_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
export const MAX_RATE_SET_NAME_LENGTH = 80;

const MONEY_RE = /^\d{1,10}(\.\d{1,2})?$/;

/** "185" -> "185.00", "95.5" -> "95.50"; null when not a money string. */
export function canonicalMoneyOrNull(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!MONEY_RE.test(s)) return null;
  const [whole = '0', frac = ''] = s.split('.');
  return `${whole.replace(/^0+(?=\d)/, '')}.${frac.padEnd(2, '0')}`;
}

/** Defensive walk over the config blob: only well-formed sets, only
 *  well-formed amounts, no set without one, first id wins. Pure. */
export function readRateSets(config: unknown): readonly RateSetWire[] {
  if (typeof config !== 'object' || config === null) return [];
  const billing = (config as Record<string, unknown>).billing;
  if (typeof billing !== 'object' || billing === null) return [];
  const v = (billing as Record<string, unknown>).rate_sets;
  if (!Array.isArray(v)) return [];
  const out: RateSetWire[] = [];
  const seen = new Set<string>();
  for (const entry of v) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === 'string' ? e.id.trim() : '';
    const name = typeof e.name === 'string' ? e.name.trim() : '';
    if (!RATE_SET_ID_RE.test(id) || name === '' || name.length > MAX_RATE_SET_NAME_LENGTH) continue;
    if (typeof e.rates !== 'object' || e.rates === null || Array.isArray(e.rates)) continue;
    const rates: Record<string, string> = {};
    for (const [bandRaw, amountRaw] of Object.entries(e.rates as Record<string, unknown>)) {
      const band = bandRaw.trim();
      const amount = canonicalMoneyOrNull(amountRaw);
      if (band === '' || amount === null) continue;
      rates[band] = amount;
    }
    if (Object.keys(rates).length === 0) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name, rates });
  }
  return out;
}

export interface RateSetBandLine {
  band: string;
  label: string;
  rate: string;
}

/**
 * What applying a set will write, one line per band IN THE SET, in the
 * tenant's vocabulary order and labeled from it. A band the set names
 * that the vocabulary no longer carries is listed last by its token,
 * so the server's refusal (422 unknown_rate_band) is never a surprise.
 */
export function rateSetBandLines(
  set: RateSetWire,
  bands: readonly RateBandOption[],
): RateSetBandLine[] {
  const lines: RateSetBandLine[] = [];
  for (const b of bands) {
    const rate = set.rates[b.value];
    if (rate !== undefined) lines.push({ band: b.value, label: b.label, rate });
  }
  const known = new Set(bands.map((b) => b.value));
  for (const [band, rate] of Object.entries(set.rates)) {
    if (!known.has(band)) lines.push({ band, label: band, rate });
  }
  return lines;
}

// ---------------------------------------------------------------------
// Wire
// ---------------------------------------------------------------------

/** Admin-console cache namespace (the rateBandsSettingsKey pattern). */
export function rateSetsKey(slug: string): readonly unknown[] {
  return ['admin-review', 'rate-sets', slug];
}

async function parseError(res: Response, label: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => null);
  }
  return new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
}

export async function fetchRateSets(slug: string, signal?: AbortSignal): Promise<RateSetWire[]> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/rate-sets`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw await parseError(res, `GET ${path}`);
  const payload = (await res.json()) as { rate_sets: RateSetWire[] };
  return payload.rate_sets;
}

export async function putRateSets(slug: string, rate_sets: RateSetWire[]): Promise<RateSetWire[]> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/rate-sets`;
  const res = await writeFetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ rate_sets }),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `PUT ${path}`);
  const payload = (await res.json()) as { rate_sets: RateSetWire[] };
  return payload.rate_sets;
}

export interface ApplyRateSetResponse {
  rates: ClientRateRowWire[];
  applied_bands: string[];
}

/** POST .../rates/apply-set — one explicit row per band in the set. */
export async function postApplyRateSet(
  slug: string,
  clientId: string,
  body: { rate_set_id: string; effective_from: string },
): Promise<ApplyRateSetResponse> {
  const path =
    `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/rates/apply-set`;
  const res = await writeFetch(path, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `POST ${path}`);
  return (await res.json()) as ApplyRateSetResponse;
}

/** The apply's fact-shaped failures, plain words. */
export function applyRateSetErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return contactSentence("Setting rates isn't part of your access. Ask {contact_billing} to set them.", contact);
    }
    if (err.status === 422) return "That rate set isn't offered any more. Pick another one from the list.";
    if (err.status === 404) return "This client record can't be found. Go back to Clients and pick the client again.";
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence(
      "The rates didn't save. Check the rate set and start date, and if it still won't save, tell {contact_system}.",
      contact,
    )
  );
}

/**
 * A value token from a set name ("Standard 2026" -> "standard_2026"),
 * the editor's suggestion for a NEW set; locked after its first save.
 */
export function slugifyRateSetName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}
